/*
 * The client model: one plain object holding everything the interface renders, and the vocabulary
 * every reducer speaks. It is deliberately data only — no methods, no class, no store — so that
 * `applyEvent(model, event)` can stay a pure function whose result is comparable and testable
 * (architecture.md §6, spec 12 §3.4).
 *
 * Identity is load-bearing: a reducer returns a new `Model` that shares the objects it did not
 * touch, so a memoised row does not re-render because a different conversation moved (REUSE C5).
 *
 * The account, group, limit and preference shapes are the generated wire types: the model stores
 * what the server sends, in the field names the server uses, so a bootstrap payload hydrates the
 * model without a translation layer and an event replaces a row whole (protocol.md §2.4).
 */

import type { Render } from "../composer/types.ts"
import type { DraftRecord, OutboxItem, TargetKey } from "../drafts/types.ts"
import type {
	AttachmentWire,
	GroupView,
	Limits,
	NotificationPrefView,
	PublicUser,
	SelfUser,
} from "../wire/core.gen.ts"

export type { AttachmentWire, GroupView, Limits, NotificationPrefView, PublicUser, SelfUser }

export type UserId = number
export type GroupId = number
export type ConversationId = number
export type MessageId = number

/**
 * The event shape a reducer dispatches on: the envelope fields that decide routing and the gap
 * rule. The aggregate `WireEvent` union of core/wire is assignable to it; a module's reducer
 * narrows to its own union. `eventId` is absent on an ephemeral frame — that absence is the marker
 * of one (protocol.md §2.4) — and `conv`/`seq` are present only where the type earns them.
 */
export interface WireEventLike {
	readonly type: string
	readonly eventId?: number | undefined
	readonly conv?: ConversationId | undefined
	readonly seq?: number | undefined
}

/**
 * The sidebar's view of a conversation, in the field names of protocol.md §2.6 so that chat's
 * richer generated `ConversationView` is assignable to it. The four kinds are the four the schema
 * allows (`ck_chat_conversation__kind`), spelled the way the wire spells them.
 */
export interface ConversationView {
	id: ConversationId
	kind: "channel" | "private" | "dm" | "groupDm"
	/** Absent on a dm and a group dm, which are named after their members (protocol.md §2.6). */
	slug?: string
	/** The address of a direct conversation, which carries no slug (`routes/links.ts`). */
	dmHandle?: string
	/** Absent on a dm and a group dm; the sidebar falls back to the members' names. */
	name?: string
	topic?: string
	/** Set while the conversation is archived; absent means live. */
	archivedAt?: number
	/** Inline while the conversation is small; omitted, and paged over HTTP, above 50 members. */
	memberIds?: UserId[]
}

/**
 * One message as the timeline renders it, in the field names of protocol.md §2.6 `MessageView` so
 * that chat's generated shape is assignable; `render` is the node tree of spec 06 §3.1. Only the
 * fields core itself reads are spelled out — the attachment list for `file.scanChanged` — and the
 * rest of the wire shape belongs to the module that renders it.
 */
export interface MessageView {
	id: MessageId
	conv: ConversationId
	seq: number
	authorId?: UserId
	createdAt: number
	editedAt?: number
	deletedAt?: number
	markdown: string
	/** Absent on a tombstone and on a system row, exactly as `render` is omitted on the wire. */
	render?: Render
	attachments?: AttachmentWire[]
	clientMsgId?: string
}

/** The loaded window of one conversation: one contiguous run of messages, ascending by seq. */
export interface MessageSlice {
	rows: MessageView[]
	firstSeq: number
	lastSeq: number
	hasMoreBefore: boolean
	/** True after a permalink jump, while newer messages than `lastSeq` exist on the server. */
	hasMoreAfter: boolean
}

/**
 * How much of a conversation is known. `cold` is the sidebar entry alone; `loading` and
 * `resyncPending` mean a tail fetch is in flight (the second after a gap); `ready` is a loaded
 * window; `error` a fetch that failed and is offered a retry.
 */
export type ConversationStatus = "cold" | "loading" | "ready" | "resyncPending" | "error"

/**
 * A conversation in the sidebar and how much of it is known. `lastSeq` is known from the bootstrap
 * even while nothing is loaded, which is what makes an unread count possible on a cold entry and
 * what the gap rule compares an arriving seq against.
 */
export interface ConversationSlice {
	meta: ConversationView
	status: ConversationStatus
	lastSeq: number
	unread: number
	mentions: number
	/**
	 * Set by the reducer that creates a slice for a membership the client did not have before.
	 * The first seq that arrives is then not gap-checked — the visible history starts wherever the
	 * server says, not at `lastSeq + 1` — and the flag is cleared (protocol.md §2.8 `justJoined`).
	 */
	justJoined?: boolean
}

export type ConnectionState = "connecting" | "open" | "catchup" | "offline" | "revoked" | "draining"

/**
 * Whether a message can leave the device in this connection state: the one rule every pending row
 * reads, so a new state reads the same in chat and in AI chats. `catchup` sends — the send is what
 * happens next — and `draining` does not: the outbox stops with the server's announcement, not only
 * with the close that follows it. Every state that does not send is one the shell's banner names.
 */
export function outboxSends(state: ConnectionState): boolean {
	return state === "open" || state === "catchup"
}

/**
 * Where the model lives between reloads: IndexedDB, or memory alone when the browser refuses site
 * data (a private window, a policy). `unknown` until the boot has tried, so no banner flashes
 * before the answer is in.
 */
export type Persistence = "unknown" | "idb" | "memory"

/*
 * Bumped whenever a stored model can no longer be hydrated; a mismatch cold-boots the client.
 *
 * 2: the composer's shipped surface changed (core.composer.source_mode, improvements round 1).
 * A warm boot restores the settings map it stored, and a changed *default* carries no
 * `ui.settings.changed` to correct it — so the cache is versioned instead and the first boot
 * after this change is a cold one. Drafts and the outbox are a database of their own and are
 * untouched (core/drafts/db.ts).
 *
 * 3: the chat module's slice gained required fields after that bump — `paging`, `opened` and
 * `gone`, then `threadTyping` — and a module slice is restored verbatim (`core/persist`), so a
 * cache written by an earlier build of the same round would reach a render with those fields
 * undefined. A cold boot is the only honest answer to a slice whose shape has moved.
 *
 * 4: a module left the build, and a cache written before restores its slice (`modules.documents`)
 * verbatim and forever, since no module is left to replace it. The cold boot
 * drops it on every device in one step; the number only rises.
 */
export const MODEL_VERSION = 4

export interface Model {
	me: SelfUser | null
	users: Record<UserId, PublicUser>
	/** Every live group; a deleted one arrives as a tombstone and is removed from here. */
	groups: Record<GroupId, GroupView>
	conversations: Record<ConversationId, ConversationSlice>
	/**
	 * The reader's own conversations in the server's order, from the bootstrap and later events.
	 * A conversation they are not in — a public channel read by its address, one they have left — is
	 * in `conversations` and deliberately not here: this is what the sidebar, the quick switcher and
	 * the landing redirect of "/" read.
	 */
	conversationOrder: ConversationId[]
	/** The loaded windows, one per conversation; a cold conversation has no entry. */
	messages: Record<ConversationId, MessageSlice>
	drafts: Record<TargetKey, DraftRecord>
	/**
	 * Every message written but not acknowledged yet, in send order — the outbox's own records
	 * (core/drafts), which are what survives a reload (spec 08, A19). The outbox owns the list and
	 * publishes it here; nothing else writes it.
	 */
	outbox: OutboxItem[]
	/** Slices owned by a module, keyed by module id — core never reads inside them. */
	modules: Record<string, unknown>
	/**
	 * The modules this instance actually runs, as the server names them, or null while it has not
	 * said — before the first bootstrap, and on a server too old to say.
	 *
	 * The frontend registers every module it was built with (`web/src/modules.ts`); which of them the
	 * *instance* runs is a line in its configuration, and until this arrived the rail drew the place
	 * of a module whose routes, actions and events do not exist on that server — a door into a page
	 * of refusals. Null means "draw everything", because saying nothing must never be what
	 * takes a place away.
	 */
	enabledModules: readonly string[] | null
	/** The account's UI settings as the bootstrap merged them over the defaults, by registry key. */
	settings: Record<string, unknown>
	notificationPrefs: NotificationPrefView | null
	/** The caps the server serves; never hard-coded on the client (protocol.md §2.5.1). */
	limits: Limits | null
	/** Unread rows of the activity feed, capped by the server. */
	activityUnread: number
	connection: ConnectionState
	persistence: Persistence
	meta: {
		/** Highest event id applied; `sync` resumes from it (protocol.md §2.8). */
		lastEventId: number
		modelVersion: number
		/** Informational: a server restart changes it, and a restart is invisible to the client. */
		serverBootId: string
	}
}

/**
 * What a reducer did with one event, and what the caller must do next. `buffered` is an event held
 * back until the conversation it belongs to has finished loading; a `resync` result asks the caller
 * to refetch — everything, or one conversation — because a gap was seen.
 */
export type Applied =
	| "applied"
	| "duplicate"
	| "buffered"
	| "unknown"
	| { resync: "all" }
	| { resync: "conv"; conv: ConversationId }

/** The empty model a cold boot starts from. */
export function emptyModel(): Model {
	return {
		me: null,
		users: {},
		groups: {},
		conversations: {},
		conversationOrder: [],
		messages: {},
		drafts: {},
		outbox: [],
		modules: {},
		enabledModules: null,
		settings: {},
		notificationPrefs: null,
		limits: null,
		activityUnread: 0,
		connection: "connecting",
		persistence: "unknown",
		meta: { lastEventId: 0, modelVersion: MODEL_VERSION, serverBootId: "" },
	}
}
